import React, { useEffect, useMemo, useState } from "react";
import {
  ShieldQuestion, Send, Repeat, Loader2, X, AlertTriangle,
  CheckCircle2, Copy, ShieldOff, FileQuestion, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListAgencies,
  adminSetReviewStatus,
  adminDispatchAlert,
  adminReassignAlert,
} from "../lib/api";

// Reviewable verification states the admin can set on an alert before
// dispatch. "Nouveau" is the implicit default — the admin sets the
// others explicitly. "Dispatchée" is *not* in this list because it's
// only reachable via the /dispatch endpoint (the backend enforces this
// by returning HTTP 400 if /review tries to set it).
const REVIEW_STATES = [
  { key: "En vérification", icon: ShieldQuestion,  tone: "amber" },
  { key: "Fausse alerte",   icon: ShieldOff,       tone: "rose"  },
  { key: "Suspecte",        icon: AlertTriangle,   tone: "rose"  },
  { key: "Doublon",         icon: Copy,            tone: "rose"  },
  { key: "Non vérifiable",  icon: FileQuestion,    tone: "zinc"  },
];

const toneClass = (tone, active) => {
  const map = {
    amber: active ? "bg-amber-500 text-black border-amber-500" : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10",
    rose:  active ? "bg-rose-500 text-white border-rose-500"   : "border-rose-500/40 text-rose-300 hover:bg-rose-500/10",
    zinc:  active ? "bg-zinc-600 text-white border-zinc-600"   : "border-zinc-700 text-zinc-400 hover:bg-zinc-800",
  };
  return map[tone] || map.zinc;
};

/**
 * Phase-2 dispatch panel that sits alongside the existing
 * AlertStatusControl. Surfaces three actions for the admin:
 *
 *   1. Set central_review_status (sub-card with 5 chips + optional note)
 *   2. Dispatch to one or more Agences (multi-select picker)
 *   3. Réaffecter (only visible once already dispatched; required reason)
 *
 * All actions hit the new Iter55 endpoints. The component is dumb
 * about layout — drop it inside any admin alert card.
 */
export const AlertDispatchPanel = ({ alert, onUpdated }) => {
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);

  const [mode, setMode] = useState(null);  // null | "review" | "dispatch" | "reassign"
  const [submitting, setSubmitting] = useState(false);

  // Review form state
  const [reviewStatus, setReviewStatus] = useState(null);
  const [reviewNote, setReviewNote] = useState("");

  // Dispatch / Reassign shared form state
  const [picked, setPicked] = useState([]);
  const [reason, setReason] = useState("");

  const current = useMemo(() => alert.central_review_status || "Nouveau", [alert]);
  const dispatchedTo = useMemo(() => alert.current_assigned_agence_ids || [], [alert]);

  useEffect(() => {
    if (mode !== "dispatch" && mode !== "reassign") return;
    if (agencies.length) return;
    setLoadingAgencies(true);
    adminListAgencies()
      .then((list) => setAgencies(list.filter((a) => a.active !== false)))
      .catch(() => setAgencies([]))
      .finally(() => setLoadingAgencies(false));
  }, [mode, agencies.length]);

  // Initialise the picker with currently-assigned agencies when opening
  // reassign so admins start from the existing set.
  useEffect(() => {
    if (mode === "reassign") {
      setPicked(dispatchedTo);
      setReason("");
    } else if (mode === "dispatch") {
      setPicked([]);
      setReason("");
    } else if (mode === "review") {
      setReviewStatus(null);
      setReviewNote("");
    }
  }, [mode, dispatchedTo]);

  const togglePick = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const submitReview = async () => {
    if (!reviewStatus) { toast.error("Sélectionnez un statut"); return; }
    setSubmitting(true);
    try {
      const updated = await adminSetReviewStatus(alert.id, reviewStatus, reviewNote);
      toast.success(`Statut → ${reviewStatus}`);
      onUpdated?.(updated);
      setMode(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally { setSubmitting(false); }
  };

  const submitDispatch = async () => {
    if (picked.length === 0) { toast.error("Choisissez au moins une Agence"); return; }
    setSubmitting(true);
    try {
      const updated = await adminDispatchAlert(alert.id, picked, reason);
      toast.success(`Alerte dispatchée vers ${picked.length} Agence${picked.length > 1 ? "s" : ""}`);
      onUpdated?.(updated);
      setMode(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch impossible");
    } finally { setSubmitting(false); }
  };

  const submitReassign = async () => {
    if (picked.length === 0) { toast.error("Choisissez au moins une Agence"); return; }
    if (reason.trim().length < 3) { toast.error("La raison de la réaffectation est obligatoire (3 caractères min.)"); return; }
    setSubmitting(true);
    try {
      const updated = await adminReassignAlert(alert.id, picked, reason);
      toast.success("Alerte réaffectée");
      onUpdated?.(updated);
      setMode(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réaffectation impossible");
    } finally { setSubmitting(false); }
  };

  const isDispatched = (alert.central_review_status === "Dispatchée") || dispatchedTo.length > 0;

  return (
    <div className="space-y-2" data-testid={`alert-dispatch-panel-${alert.id}`}>
      {/* Status header */}
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
        <span className="text-zinc-400">Vérif. centrale :</span>
        <span
          data-testid={`alert-review-status-${alert.id}`}
          className={`px-2 py-0.5 border ${
            current === "Dispatchée"      ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" :
            current === "Fausse alerte"   ? "bg-rose-500/15 border-rose-500/40 text-rose-300" :
            current === "Suspecte"        ? "bg-rose-500/15 border-rose-500/40 text-rose-300" :
            current === "Doublon"         ? "bg-rose-500/15 border-rose-500/40 text-rose-300" :
            current === "Non vérifiable"  ? "bg-zinc-600/30 border-zinc-600 text-zinc-300" :
            current === "En vérification" ? "bg-amber-500/15 border-amber-500/40 text-amber-300" :
                                            "bg-sky-500/15 border-sky-500/40 text-sky-300"
          }`}
        >
          {current}
        </span>
        {alert.operational_status && (
          <>
            <span className="text-zinc-600">·</span>
            <span className="text-zinc-400">Op :</span>
            <span className="px-2 py-0.5 border border-emerald-500/40 text-emerald-300 bg-emerald-500/15">
              {alert.operational_status}
            </span>
          </>
        )}
      </div>

      {/* Action row */}
      {!mode && (
        <div className="grid grid-cols-3 gap-1.5">
          <button
            type="button"
            data-testid={`alert-review-btn-${alert.id}`}
            onClick={() => setMode("review")}
            className="border border-amber-500/40 text-amber-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-amber-500/10 transition-colors flex items-center justify-center gap-1.5"
          >
            <ShieldQuestion className="w-3 h-3" /> Vérifier
          </button>
          <button
            type="button"
            data-testid={`alert-dispatch-btn-${alert.id}`}
            onClick={() => setMode("dispatch")}
            className="border border-emerald-500/40 text-emerald-300 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-emerald-500 hover:text-black transition-colors flex items-center justify-center gap-1.5"
          >
            <Send className="w-3 h-3" /> Dispatcher
          </button>
          <button
            type="button"
            data-testid={`alert-reassign-btn-${alert.id}`}
            disabled={!isDispatched}
            onClick={() => setMode("reassign")}
            className={`border py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors flex items-center justify-center gap-1.5 ${
              isDispatched
                ? "border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                : "border-zinc-800 text-zinc-700 cursor-not-allowed"
            }`}
            title={isDispatched ? "Réaffecter vers une autre Agence" : "Disponible uniquement après dispatch"}
          >
            <Repeat className="w-3 h-3" /> Réaffecter
          </button>
        </div>
      )}

      {/* REVIEW form */}
      {mode === "review" && (
        <div className="border border-amber-500/40 bg-amber-500/5 p-2 space-y-2" data-testid={`alert-review-form-${alert.id}`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300 flex items-center gap-1.5">
              <ShieldQuestion className="w-3 h-3" /> Vérification — choisissez un statut
            </span>
            <button type="button" onClick={() => setMode(null)} className="text-zinc-500 hover:text-zinc-200"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {REVIEW_STATES.map(({ key, icon: Icon, tone }) => (
              <button
                key={key}
                type="button"
                data-testid={`alert-review-chip-${key}`}
                onClick={() => setReviewStatus(key)}
                className={`py-1.5 px-2 text-[10px] font-bold uppercase tracking-[0.12em] border flex items-center justify-center gap-1.5 transition-colors ${toneClass(tone, reviewStatus === key)}`}
              >
                <Icon className="w-3 h-3" /> {key}
              </button>
            ))}
          </div>
          <textarea
            data-testid={`alert-review-note-${alert.id}`}
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            rows={2}
            placeholder="Note interne (visible uniquement par le Central Admin)"
            className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-amber-500 focus:outline-none p-2 text-[11px] resize-none"
          />
          <button
            type="button"
            data-testid={`alert-review-submit-${alert.id}`}
            disabled={submitting || !reviewStatus}
            onClick={submitReview}
            className="w-full bg-amber-500 text-black py-2 text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-amber-400 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Enregistrer
          </button>
        </div>
      )}

      {/* DISPATCH / REASSIGN form */}
      {(mode === "dispatch" || mode === "reassign") && (
        <div className={`border p-2 space-y-2 ${mode === "dispatch" ? "border-emerald-500/40 bg-emerald-500/5" : "border-sky-500/40 bg-sky-500/5"}`} data-testid={`alert-${mode}-form-${alert.id}`}>
          <div className="flex items-center justify-between">
            <span className={`text-[10px] font-bold uppercase tracking-[0.25em] ${mode === "dispatch" ? "text-emerald-300" : "text-sky-300"} flex items-center gap-1.5`}>
              {mode === "dispatch" ? <><Send className="w-3 h-3" /> Choisir la / les Agence(s)</> : <><Repeat className="w-3 h-3" /> Réaffecter à</>}
            </span>
            <button type="button" onClick={() => setMode(null)} className="text-zinc-500 hover:text-zinc-200"><X className="w-3.5 h-3.5" /></button>
          </div>
          {loadingAgencies && <div className="text-[11px] text-zinc-500">Chargement…</div>}
          {!loadingAgencies && agencies.length === 0 && (
            <div className="text-[11px] text-rose-300">Aucune Agence active. Créez-en une dans l'onglet Agences.</div>
          )}
          {!loadingAgencies && agencies.length > 0 && (
            <div className="flex flex-wrap gap-1 max-h-40 overflow-auto">
              {agencies.map((a) => {
                const active = picked.includes(a.id);
                const wasPrevious = (alert.previous_agence_ids || []).includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`alert-pick-agency-${a.id}`}
                    onClick={() => togglePick(a.id)}
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] border flex items-center gap-1 transition-colors ${
                      active
                        ? "bg-emerald-500 text-black border-emerald-500"
                        : "border-zinc-700 text-zinc-300 hover:border-emerald-500 hover:text-emerald-300"
                    } ${wasPrevious && !active ? "opacity-70 italic" : ""}`}
                    title={wasPrevious ? "Précédemment assignée" : ""}
                  >
                    {a.name}
                    {wasPrevious && <span className="text-zinc-500 text-[9px]">↺</span>}
                  </button>
                );
              })}
            </div>
          )}
          <textarea
            data-testid={`alert-${mode}-reason-${alert.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder={mode === "reassign" ? "Raison de la réaffectation (obligatoire, 3 caractères min.)" : "Note de dispatch (facultatif)"}
            className="w-full border border-zinc-700 bg-black/40 text-zinc-100 placeholder-zinc-600 focus:border-emerald-500 focus:outline-none p-2 text-[11px] resize-none"
          />
          <button
            type="button"
            data-testid={`alert-${mode}-submit-${alert.id}`}
            disabled={submitting || picked.length === 0 || (mode === "reassign" && reason.trim().length < 3)}
            onClick={mode === "dispatch" ? submitDispatch : submitReassign}
            className={`w-full py-2 text-[10px] font-bold uppercase tracking-[0.15em] transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5 ${
              mode === "dispatch"
                ? "bg-emerald-500 text-black hover:bg-emerald-400"
                : "bg-sky-500 text-white hover:bg-sky-400"
            }`}
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : (mode === "dispatch" ? <Send className="w-3 h-3" /> : <Repeat className="w-3 h-3" />)}
            {mode === "dispatch" ? `Dispatcher (${picked.length})` : `Réaffecter (${picked.length})`}
          </button>
        </div>
      )}

      {/* Reassignment history mini-list */}
      {(alert.reassignment_history || []).length > 0 && !mode && (
        <details className="border border-zinc-800 bg-black/30 p-2" data-testid={`alert-reassign-history-${alert.id}`}>
          <summary className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 cursor-pointer">
            Historique des réaffectations ({alert.reassignment_history.length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {alert.reassignment_history.map((r, i) => (
              <li key={i} className="text-[11px] text-zinc-400 border-l-2 border-sky-500/40 pl-2">
                <div className="text-zinc-500 font-mono text-[10px]">{new Date(r.reassigned_at).toLocaleString("fr-FR")}</div>
                <div className="italic">"{r.reassignment_reason}"</div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
